/**
 * Aligns the left and right insole streams onto a single shared clock so the two
 * feet stay in sync during playback.
 *
 * Each insole timestamps against its own free-running counter (`ts`), and the two
 * counters are unrelated — they can be minutes or hours apart, and the streams
 * can have different sample counts. Pairing the feet by array index (what the
 * player did originally) therefore drifts: with unequal counts the paired frames
 * slide apart until the feet appear to move together ("jumping").
 *
 * The fix is to put both feet on one real-world clock and then pair by *time*:
 *
 *  - **UTC (preferred):** each stream carries a `logmsg` entry that correlates an
 *    insole `ts` to a UTC datetime. `offset = utc(logmsg) - logmsg.ts` converts
 *    every sample's `ts` to UTC. Both feet then share the world clock regardless
 *    of counter bases.
 *  - **Relative (fallback):** if a `logmsg` is missing or clearly inconsistent
 *    with its own samples (e.g. its `ts` sits hours outside the sample range — a
 *    bad/old recording), anchoring on it would throw that foot far off. We fall
 *    back to treating each stream's first sample as t=0, which keeps the feet
 *    overlapping and in sync by relative time. A warning records why.
 *
 * Frames are then the time-sorted union of both feet's samples; each frame holds
 * the nearest left and right sample to that instant. Pairing by nearest time
 * means a sample-count mismatch no longer causes drift.
 */

import { InsoleDataEntry, PressureTimelineData } from './PressureTimelineViewer';

/** A logmsg whose `ts` may legitimately sit this far outside the sample range. */
const LOGMSG_RANGE_MARGIN_MS = 60_000;

interface RawSample {
  ts: number;
  ps: number[];
}

interface LogMsg {
  ts: number;
  utc: number;
}

export interface AlignedFrame {
  /** Instant on the shared clock (UTC ms, or relative ms in the fallback). */
  t: number;
  /** Nearest left-foot pressure frame to `t`, or null if the foot has no data. */
  leftPs: number[] | null;
  /** Nearest right-foot pressure frame to `t`, or null if the foot has no data. */
  rightPs: number[] | null;
}

/**
 * Stable codes for data-quality notes. The model stays i18n-agnostic (and easy
 * to unit-test): it emits codes, and the UI maps them to localised text via
 * `pressureTimeline.warnings.<code>`.
 */
export type PressureTimelineWarning =
  | 'leftLogmsgInconsistent'
  | 'rightLogmsgInconsistent';

export interface PressureTimelineModel {
  frames: AlignedFrame[];
  /** `frames.map(f => f.t)` — the shared-clock instants, ascending. */
  times: number[];
  startMs: number;
  durationMs: number;
  alignment: 'utc' | 'relative';
  /** Codes for data issues (e.g. an inconsistent logmsg), localised by the UI. */
  warnings: PressureTimelineWarning[];
}

/** Parse the UTC instant from a logmsg string like `2026-09-19T17:23:52.65Z+02:00`. */
export function parseLogMsgUtc(value: string | undefined): number | null {
  if (!value) return null;
  const zIndex = value.indexOf('Z');
  const iso = zIndex >= 0 ? value.slice(0, zIndex + 1) : value;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

function extractSamples(entries: InsoleDataEntry[]): RawSample[] {
  const samples: RawSample[] = [];
  for (const e of entries) {
    const ps = e?.semiRawImuPs?.ps;
    if (ps && typeof e.ts === 'number') {
      samples.push({ ts: e.ts, ps });
    }
  }
  samples.sort((a, b) => a.ts - b.ts);
  return samples;
}

function extractLogMsg(entries: InsoleDataEntry[]): LogMsg | null {
  const entry = entries?.find((e) => e?.logmsg);
  if (!entry?.logmsg || typeof entry.logmsg.ts !== 'number') return null;
  const utc = parseLogMsgUtc(entry.logmsg.string);
  return utc === null ? null : { ts: entry.logmsg.ts, utc };
}

/** A logmsg is trustworthy only if its `ts` sits within (± margin of) its samples. */
function isLogMsgConsistent(log: LogMsg | null, samples: RawSample[]): boolean {
  if (!log || samples.length === 0) return false;
  const first = samples[0].ts;
  const last = samples[samples.length - 1].ts;
  return (
    log.ts >= first - LOGMSG_RANGE_MARGIN_MS &&
    log.ts <= last + LOGMSG_RANGE_MARGIN_MS
  );
}

interface AlignedSamples {
  t: number;
  ps: number[];
}

/** Nearest sample to time `t` in a list sorted ascending by `t`. */
function nearest(arr: AlignedSamples[], t: number): number[] | null {
  if (arr.length === 0) return null;
  if (t <= arr[0].t) return arr[0].ps;
  if (t >= arr[arr.length - 1].t) return arr[arr.length - 1].ps;

  let lo = 0;
  let hi = arr.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid].t === t) return arr[mid].ps;
    if (arr[mid].t < t) lo = mid + 1;
    else hi = mid - 1;
  }
  // lo is the insertion point; the nearest is arr[hi] or arr[lo].
  const below = arr[hi];
  const above = arr[lo];
  return t - below.t <= above.t - t ? below.ps : above.ps;
}

export function buildPressureTimelineModel(
  data: PressureTimelineData
): PressureTimelineModel {
  const leftSamples = extractSamples(data?.leftInsole?.data ?? []);
  const rightSamples = extractSamples(data?.rightInsole?.data ?? []);

  const leftLog = extractLogMsg(data?.leftInsole?.data ?? []);
  const rightLog = extractLogMsg(data?.rightInsole?.data ?? []);

  const leftLogOk = isLogMsgConsistent(leftLog, leftSamples);
  const rightLogOk = isLogMsgConsistent(rightLog, rightSamples);

  const warnings: PressureTimelineWarning[] = [];
  if (leftLog && !leftLogOk) {
    warnings.push('leftLogmsgInconsistent');
  }
  if (rightLog && !rightLogOk) {
    warnings.push('rightLogmsgInconsistent');
  }

  // Use the real UTC clock only when BOTH feet have a trustworthy logmsg;
  // otherwise a single bad anchor would push one foot far from the other, so
  // fall back to relative time (each foot's first sample = 0) for both.
  const useUtc =
    leftLogOk && rightLogOk && leftSamples.length > 0 && rightSamples.length > 0;

  let leftOffset: number;
  let rightOffset: number;
  if (useUtc) {
    leftOffset = leftLog!.utc - leftLog!.ts;
    rightOffset = rightLog!.utc - rightLog!.ts;
  } else {
    leftOffset = leftSamples.length ? -leftSamples[0].ts : 0;
    rightOffset = rightSamples.length ? -rightSamples[0].ts : 0;
  }

  const left: AlignedSamples[] = leftSamples.map((s) => ({
    t: s.ts + leftOffset,
    ps: s.ps,
  }));
  const right: AlignedSamples[] = rightSamples.map((s) => ({
    t: s.ts + rightOffset,
    ps: s.ps,
  }));

  // Master timeline = time-sorted, de-duplicated union of both feet's instants.
  const timeSet = new Set<number>();
  for (const s of left) timeSet.add(s.t);
  for (const s of right) timeSet.add(s.t);
  const times = Array.from(timeSet).sort((a, b) => a - b);

  const frames: AlignedFrame[] = times.map((t) => ({
    t,
    leftPs: nearest(left, t),
    rightPs: nearest(right, t),
  }));

  const startMs = times.length ? times[0] : 0;
  const durationMs = times.length ? times[times.length - 1] - startMs : 0;

  return {
    frames,
    times,
    startMs,
    durationMs,
    alignment: useUtc ? 'utc' : 'relative',
    warnings,
  };
}
